import React from 'react';
import JsonView from '@uiw/react-json-view';
import { Box, Typography, Chip } from '@mui/material';

interface JsonViewerProps {
  /** Dados JSON para exibir */
  data: unknown;
  /** Nome da variável (opcional) */
  name?: string;
  /** Tipo da variável */
  type?: string;
  /** Se deve mostrar o cabeçalho com nome e tipo */
  showHeader?: boolean;
  /** Altura máxima do componente */
  maxHeight?: string;
}

/**
 * Componente para exibir JSON de forma amigável
 */
export const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  name,
  type,
  showHeader = false,
  maxHeight = '400px'
}) => {
  // Função para determinar se o dado é JSON válido
  const isJsonString = (value: unknown): value is string => {
    if (typeof value !== 'string') return false;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  };

  // Função para processar o valor
  const processValue = (value: unknown): unknown => {
    if (isJsonString(value)) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  };

  const processedData = processValue(data);

  return (
    <Box>
      {showHeader && (name || type) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {name && (
            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {name}
            </Typography>
          )}
          {type && (
            <Chip
              label={type}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>
      )}
      
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          maxHeight,
          '& .w-json-view-container': {
            backgroundColor: 'background.paper',
          },
          '& .w-json-view-key': {
            color: 'primary.main',
            fontFamily: 'monospace',
          },
          '& .w-json-view-string': {
            color: 'success.main',
            fontFamily: 'monospace',
          },
          '& .w-json-view-number': {
            color: 'info.main',
            fontFamily: 'monospace',
          },
          '& .w-json-view-boolean': {
            color: 'warning.main',
            fontFamily: 'monospace',
          },
          '& .w-json-view-null': {
            color: 'text.disabled',
            fontFamily: 'monospace',
          },
        }}
      >
        <JsonView
          value={processedData as object}
          style={{
            backgroundColor: 'transparent',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
          collapsed={false}
          displayDataTypes={false}
          displayObjectSize={false}
          enableClipboard={true}
        />
      </Box>
    </Box>
  );
};

export default JsonViewer;
